import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@material-ui/core'
import { ChevronDown } from 'react-feather'
import { useTranslation } from 'react-i18next'
import {
  HourSeriesCollectionFragment,
  HourSeriesFragment,
} from '../../generated/graphql'
import { HourSeriesList } from './HourSeriesList'
import { useAlert } from '../alert/AlertProvider'

type Props = {
  collections: HourSeriesCollectionFragment[]
  onDeleteCollection: (
    collection: HourSeriesCollectionFragment,
  ) => Promise<void>
  onDeleteHourSeries: (
    collection: HourSeriesCollectionFragment,
    hourSeries: HourSeriesFragment,
  ) => Promise<void>
  editDisabled?: boolean
}

export function HourSeriesCollectionList({
  collections,
  onDeleteCollection,
  onDeleteHourSeries,
  editDisabled = false,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { alert } = useAlert()

  const handleDeleteCollection = (collection: HourSeriesCollectionFragment) => {
    alert(
      t('projectHourSeries.deleteCollectionDialog.title'),
      t('projectHourSeries.deleteCollectionDialog.message'),
      [
        { title: t('common.cancel') },
        {
          title: t('projectHourSeries.deleteCollectionDialog.confirm'),
          color: 'secondary',
          onPress: async () => {
            await onDeleteCollection(collection)
          },
        },
      ],
    )
  }

  const collectionEditDisabled = (collection: HourSeriesCollectionFragment) =>
    editDisabled || collection.isInUse

  return (
    <Box>
      {collections.map((collection) => (
        <Accordion
          key={collection.id}
          id={`collection-${collection.name}-accordion`}
        >
          <AccordionSummary
            expandIcon={
              <ChevronDown id={`collection-${collection.name}-expand-button`} />
            }
          >
            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography variant="h6">{collection.name}</Typography>
              <Box>
                {!collectionEditDisabled(collection) && (
                  <Button
                    id={`collection-${collection.name}-remove-button`}
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleDeleteCollection(collection)
                    }}
                  >
                    {t('projectHourSeries.deleteCollection')}
                  </Button>
                )}
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <HourSeriesList
              hourSeries={collection.hourSeries}
              onDeleteHourSeries={
                editDisabled
                  ? undefined
                  : (hourSeries) => onDeleteHourSeries(collection, hourSeries)
              }
            />
          </AccordionDetails>
        </Accordion>
      ))}

      {collections.length === 0 ? (
        <Typography>{t('projectHourSeries.noHourSeriesInList')}</Typography>
      ) : null}
    </Box>
  )
}
